import { mulberry32, randInt, pickOne, shuffle, randomSeed } from "./rng.js";

const VALID_OPS = ["+", "−", "×", "÷"];
const MAX_VAL = 100;

const SIZE_MAP = {
  small:  { equations: 5,  maxDim: 9,  decoys: 3 },
  medium: { equations: 7,  maxDim: 11, decoys: 4 },
  large:  { equations: 10, maxDim: 13, decoys: 5 },
};

const NUMERIC_POS = [0, 2, 4];

export function generate({ ops, size = "medium", seed, maxCols, maxRows } = {}) {
  const activeOps = (ops && ops.length ? ops : VALID_OPS).filter((o) => VALID_OPS.includes(o));
  if (activeOps.length === 0) activeOps.push("+");
  const base = SIZE_MAP[size] ?? SIZE_MAP.medium;
  const cfg = {
    ...base,
    maxCols: Math.max(5, Math.min(base.maxDim, maxCols ?? base.maxDim)),
    maxRows: Math.max(5, Math.min(base.maxDim, maxRows ?? base.maxDim)),
  };
  const baseSeed = seed ?? randomSeed();

  for (let attempt = 0; attempt < 50; attempt++) {
    const rng = mulberry32((baseSeed + attempt * 7919) >>> 0);
    const puzzle = tryBuild(activeOps, cfg, rng);
    if (puzzle) return puzzle;
  }
  throw new Error("math_crossword: generator exhausted attempts");
}

function tryBuild(ops, cfg, rng) {
  const cells = new Map();
  const equations = [];

  const seedEq = equationWithValueAt(ops, null, null, rng);
  if (!seedEq) return null;
  seedEq.orientation = "h";
  seedEq.startRow = 0;
  seedEq.startCol = 0;
  if (!placeEquation(cells, equations, seedEq, cfg)) return null;

  let guard = 0;
  while (equations.length < cfg.equations && guard < 1200) {
    guard++;
    if (!growOnce(cells, equations, ops, cfg, rng)) {
      // nothing fit this pass; keep trying with different randomness
    }
  }
  if (equations.length < cfg.equations) return null;

  // Densify: try to add equations that cross multiple existing cells.
  densify(cells, equations, ops, cfg, rng);

  const { rows, cols } = normalize(cells, equations);
  const materialized = materialize(cells, equations, rows, cols, rng);
  if (!materialized) return null;
  const { grid, blanks } = materialized;

  // Cap pool duplicates: no answer should need to appear more than twice.
  const counts = new Map();
  for (const b of blanks) counts.set(b.answer, (counts.get(b.answer) ?? 0) + 1);
  for (const n of counts.values()) if (n > 2) return null;

  const pool = buildPool(blanks, cfg, rng);
  return { rows, cols, grid, blanks, pool };
}

// Scores a candidate equation placement without mutating state.
function scorePlacement(cells, eq, cfg) {
  const [dr, dc] = eq.orientation === "h" ? [0, 1] : [1, 0];
  const values = [eq.values.a, eq.values.op, eq.values.b, "=", eq.values.c];
  const types = ["given", "op", "given", "op", "given"];
  let crossings = 0;
  const positions = [];

  for (let i = 0; i < 5; i++) {
    const r = eq.startRow + dr * i;
    const c = eq.startCol + dc * i;
    positions.push([r, c]);
    const existing = cells.get(`${r},${c}`);
    if (existing) {
      if (types[i] === "given" && existing.type === "given" && existing.value === String(values[i])) {
        crossings++;
      } else {
        return { valid: false };
      }
    }
  }

  // Prevent chaining: no cell immediately before the start or after the end
  // on this line (same orientation) — otherwise we'd concatenate two equations.
  const beforeKey = `${eq.startRow - dr},${eq.startCol - dc}`;
  const afterKey = `${eq.startRow + dr * 5},${eq.startCol + dc * 5}`;
  if (cells.has(beforeKey) || cells.has(afterKey)) return { valid: false };

  const bounds = computeBounds(cells);
  for (const [r, c] of positions) {
    bounds.minR = Math.min(bounds.minR, r);
    bounds.minC = Math.min(bounds.minC, c);
    bounds.maxR = Math.max(bounds.maxR, r);
    bounds.maxC = Math.max(bounds.maxC, c);
  }
  const width = bounds.maxC - bounds.minC + 1;
  const height = bounds.maxR - bounds.minR + 1;
  if (width > cfg.maxCols || height > cfg.maxRows) return { valid: false };

  return { valid: true, crossings, area: width * height };
}

function growOnce(cells, equations, ops, cfg, rng) {
  const candidates = [];

  const numericKeys = [];
  for (const [key, cell] of cells) {
    if (cell.type === "given") numericKeys.push(key);
  }
  shuffle(numericKeys, rng);

  for (const key of numericKeys) {
    const [r, c] = key.split(",").map(Number);
    const cell = cells.get(key);
    const v = Number(cell.value);
    const usedOrients = new Set();
    for (const eqIdx of cell.equations) usedOrients.add(equations[eqIdx].orientation);
    const orients = ["h", "v"].filter((o) => !usedOrients.has(o));
    shuffle(orients, rng);

    for (const orient of orients) {
      for (const pos of shuffle([...NUMERIC_POS], rng)) {
        for (let t = 0; t < 4; t++) {
          const eq = equationWithValueAt(ops, v, pos, rng);
          if (!eq) continue;
          const [dr, dc] = orient === "h" ? [0, 1] : [1, 0];
          eq.orientation = orient;
          eq.startRow = r - dr * pos;
          eq.startCol = c - dc * pos;
          const score = scorePlacement(cells, eq, cfg);
          if (score.valid) candidates.push({ eq, ...score });
        }
      }
    }
    if (candidates.length >= 40) break;
  }

  if (candidates.length === 0) return false;
  candidates.sort((a, b) => b.crossings - a.crossings || a.area - b.area);
  return placeEquation(cells, equations, candidates[0].eq, cfg);
}

function densify(cells, equations, ops, cfg, rng) {
  const targetBonus = Math.ceil(cfg.equations * 0.6);
  const cap = equations.length + targetBonus;
  let attempts = 0;

  while (equations.length < cap && attempts < 800) {
    attempts++;
    const candidate = findMultiCrossCandidate(cells, equations, ops, cfg, rng);
    if (!candidate) break;
    if (!placeEquation(cells, equations, candidate.eq, cfg)) break;
  }
}

function findMultiCrossCandidate(cells, equations, ops, cfg, rng) {
  const numeric = [];
  for (const [key, cell] of cells) {
    if (cell.type === "given") {
      const [r, c] = key.split(",").map(Number);
      numeric.push({ r, c, value: Number(cell.value) });
    }
  }
  if (numeric.length < 2) return null;

  const pairs = [];
  for (let i = 0; i < numeric.length; i++) {
    for (let j = i + 1; j < numeric.length; j++) {
      const a = numeric[i], b = numeric[j];
      if (a.r === b.r) {
        const d = Math.abs(a.c - b.c);
        if (d === 2 || d === 4) pairs.push({ a, b, orientation: "h", delta: d });
      } else if (a.c === b.c) {
        const d = Math.abs(a.r - b.r);
        if (d === 2 || d === 4) pairs.push({ a, b, orientation: "v", delta: d });
      }
    }
  }
  if (pairs.length === 0) return null;
  shuffle(pairs, rng);

  for (const pair of pairs) {
    const eq = equationThroughPair(pair, ops, cells, cfg, rng);
    if (eq) {
      const score = scorePlacement(cells, eq, cfg);
      if (score.valid && score.crossings >= 2) return { eq, ...score };
    }
  }
  return null;
}

function equationThroughPair(pair, ops, cells, cfg, rng) {
  const { a, b, orientation, delta } = pair;
  const horiz = orientation === "h";
  const [first, second] = horiz
    ? (a.c <= b.c ? [a, b] : [b, a])
    : (a.r <= b.r ? [a, b] : [b, a]);

  const subcases = delta === 2 ? [[0, 2], [2, 4]] : [[0, 4]];

  for (const [p1, p2] of shuffle([...subcases], rng)) {
    const startRow = horiz ? first.r : first.r - p1;
    const startCol = horiz ? first.c - p1 : first.c;
    const otherPos = [0, 2, 4].find((p) => p !== p1 && p !== p2);

    for (const op of shuffle([...ops], rng)) {
      const vals = { 0: null, 2: null, 4: null };
      vals[p1] = first.value;
      vals[p2] = second.value;

      const vA = vals[0], vB = vals[2], vC = vals[4];
      let solved;
      if (otherPos === 0) solved = solveForA(op, vB, vC);
      else if (otherPos === 2) solved = solveForB(op, vA, vC);
      else solved = solveForC(op, vA, vB);
      if (solved === null) continue;

      const fullA = otherPos === 0 ? solved : vA;
      const fullB = otherPos === 2 ? solved : vB;
      const fullC = otherPos === 4 ? solved : vC;

      if (!validEquation(op, fullA, fullB, fullC)) continue;

      const [dr, dc] = horiz ? [0, 1] : [1, 0];
      const opCells = [1, 3].map((i) => [startRow + dr * i, startCol + dc * i]);
      const numCell = [startRow + dr * otherPos, startCol + dc * otherPos];
      // operator positions must be empty
      if (opCells.some(([r, c]) => cells.has(`${r},${c}`))) continue;
      // the missing numeric slot may be empty OR already contain the right number
      const existingNum = cells.get(`${numCell[0]},${numCell[1]}`);
      if (existingNum && !(existingNum.type === "given" && existingNum.value === String(solved))) continue;

      return {
        orientation,
        startRow,
        startCol,
        values: { a: String(fullA), b: String(fullB), c: String(fullC), op },
      };
    }
  }
  return null;
}

function solveForA(op, b, c) {
  if (op === "+") return c - b;
  if (op === "−") return c + b;
  if (op === "×") return b !== 0 && c % b === 0 ? c / b : null;
  if (op === "÷") return b * c;
  return null;
}
function solveForB(op, a, c) {
  if (op === "+") return c - a;
  if (op === "−") return a - c;
  if (op === "×") return a !== 0 && c % a === 0 ? c / a : null;
  if (op === "÷") return c !== 0 && a % c === 0 ? a / c : null;
  return null;
}
function solveForC(op, a, b) {
  if (op === "+") return a + b;
  if (op === "−") return a - b;
  if (op === "×") return a * b;
  if (op === "÷") return b !== 0 && a % b === 0 ? a / b : null;
  return null;
}
function validEquation(op, a, b, c) {
  if (!Number.isInteger(a) || !Number.isInteger(b) || !Number.isInteger(c)) return false;
  if (a < 1 || b < 1 || c < 1) return false;
  if (a > MAX_VAL || b > MAX_VAL || c > MAX_VAL) return false;
  if (op === "+") return a + b === c;
  if (op === "−") return a - b === c;
  if (op === "×") return a * b === c;
  if (op === "÷") return b !== 0 && a / b === c;
  return false;
}

function placeEquation(cells, equations, eq, cfg) {
  const score = scorePlacement(cells, eq, cfg);
  if (!score.valid) return false;

  const [dr, dc] = eq.orientation === "h" ? [0, 1] : [1, 0];
  const values = [eq.values.a, eq.values.op, eq.values.b, "=", eq.values.c];
  const types = ["given", "op", "given", "op", "given"];
  const eqIdx = equations.length;
  const cellRefs = [];

  for (let i = 0; i < 5; i++) {
    const r = eq.startRow + dr * i;
    const c = eq.startCol + dc * i;
    const key = `${r},${c}`;
    if (!cells.has(key)) {
      cells.set(key, {
        type: types[i],
        value: String(values[i]),
        equations: new Set([eqIdx]),
      });
    } else {
      cells.get(key).equations.add(eqIdx);
    }
    cellRefs.push({ r, c, role: types[i] });
  }
  equations.push({
    orientation: eq.orientation,
    values: eq.values,
    cells: cellRefs,
  });
  return true;
}

function computeBounds(cells) {
  if (cells.size === 0) return { minR: 0, minC: 0, maxR: 0, maxC: 0 };
  let minR = Infinity, minC = Infinity, maxR = -Infinity, maxC = -Infinity;
  for (const key of cells.keys()) {
    const [r, c] = key.split(",").map(Number);
    if (r < minR) minR = r;
    if (c < minC) minC = c;
    if (r > maxR) maxR = r;
    if (c > maxC) maxC = c;
  }
  return { minR, minC, maxR, maxC };
}

function normalize(cells, equations) {
  const { minR, minC, maxR, maxC } = computeBounds(cells);
  const shifted = new Map();
  for (const [key, v] of cells) {
    const [r, c] = key.split(",").map(Number);
    shifted.set(`${r - minR},${c - minC}`, v);
  }
  cells.clear();
  for (const [k, v] of shifted) cells.set(k, v);
  for (const eq of equations) {
    for (const cell of eq.cells) {
      cell.r -= minR;
      cell.c -= minC;
    }
  }
  return { rows: maxR - minR + 1, cols: maxC - minC + 1 };
}

function materialize(cells, equations, rows, cols, rng) {
  const numericKeys = (eq) => eq.cells.filter((c) => c.role === "given").map((c) => `${c.r},${c.c}`);
  const eqHasBlank = (isBlank, eq) => numericKeys(eq).some((k) => isBlank.get(k));
  const eqHasGiven = (isBlank, eq) => numericKeys(eq).some((k) => !isBlank.get(k));

  const isBlank = new Map();
  for (const [key, v] of cells) if (v.type === "given") isBlank.set(key, false);

  // Pass 1: each equation gets exactly one cell mandatorily blanked.
  for (const eq of equations) {
    const keys = numericKeys(eq);
    if (keys.some((k) => isBlank.get(k))) continue;
    isBlank.set(pickOne(keys, rng), true);
  }

  // Pass 2: coin-flip remaining cells toward blank for harder boards.
  for (const key of isBlank.keys()) {
    if (!isBlank.get(key) && rng() < 0.35) {
      isBlank.set(key, true);
      if (!equations.every((e) => eqHasGiven(isBlank, e))) isBlank.set(key, false);
    }
  }

  // Final verification — every equation must have ≥1 blank and ≥1 given.
  // If we can't satisfy both, signal failure so the outer loop regenerates.
  for (const eq of equations) {
    if (!eqHasBlank(isBlank, eq) || !eqHasGiven(isBlank, eq)) return null;
  }

  const grid = Array.from({ length: rows }, () => Array(cols).fill(null));
  const blanks = [];
  for (const [key, v] of cells) {
    const [r, c] = key.split(",").map(Number);
    if (v.type === "op") {
      grid[r][c] = { type: "op", value: v.value };
    } else if (isBlank.get(key)) {
      grid[r][c] = { type: "blank", answer: v.value };
      blanks.push({ r, c, answer: v.value });
    } else {
      grid[r][c] = { type: "given", value: v.value };
    }
  }
  return { grid, blanks };
}

function buildPool(blanks, cfg, rng) {
  const pool = blanks.map((b) => b.answer);
  const answerSet = new Set(pool);
  const decoyPool = [];
  for (let n = 1; n <= 50 && decoyPool.length < cfg.decoys * 4; n++) {
    if (!answerSet.has(String(n))) decoyPool.push(String(n));
  }
  shuffle(decoyPool, rng);
  for (let i = 0; i < cfg.decoys && i < decoyPool.length; i++) pool.push(decoyPool[i]);
  pool.sort((a, b) => Number(a) - Number(b));
  return pool;
}

function equationWithValueAt(ops, knownValue, pos, rng) {
  const op = pickOne(ops, rng);
  const free = knownValue === null || pos === null;
  let a, b, c;

  if (free) {
    ({ a, b, c } = randomSimpleEquation(op, rng));
  } else {
    const v = knownValue;
    switch (op) {
      case "+":
        if (pos === 0) { a = v; b = randInt(rng, 1, 15); c = a + b; }
        else if (pos === 2) { a = randInt(rng, 1, 15); b = v; c = a + b; }
        else { c = v; if (v < 2) return null; a = randInt(rng, 1, v - 1); b = v - a; if (a < 1 || b < 1) return null; }
        break;
      case "−":
        if (pos === 0) { a = v; if (a < 2) return null; b = randInt(rng, 1, a - 1); c = a - b; }
        else if (pos === 2) { b = v; a = randInt(rng, v + 1, v + 15); c = a - b; }
        else { c = v; b = randInt(rng, 1, 15); a = v + b; }
        break;
      case "×":
        if (pos === 0) { a = v; if (a < 2) return null; b = randInt(rng, 2, 12); c = a * b; }
        else if (pos === 2) { b = v; if (b < 2) return null; a = randInt(rng, 2, 12); c = a * b; }
        else {
          c = v;
          const divs = divisorsBetween(v, 2, 12);
          if (divs.length < 2) return null;
          a = pickOne(divs, rng);
          b = c / a;
          if (b < 2 || b > 12) return null;
        }
        break;
      case "÷":
        if (pos === 0) {
          a = v;
          const divs = divisorsBetween(v, 2, 12);
          if (divs.length === 0) return null;
          b = pickOne(divs, rng);
          c = a / b;
          if (c < 2) return null;
        } else if (pos === 2) {
          b = v;
          if (b < 2 || b > 12) return null;
          c = randInt(rng, 2, 12);
          a = b * c;
        } else {
          c = v;
          if (c < 2) return null;
          b = randInt(rng, 2, 12);
          a = b * c;
        }
        break;
    }
  }

  if (!Number.isInteger(a) || !Number.isInteger(b) || !Number.isInteger(c)) return null;
  if (a < 1 || b < 1 || c < 1) return null;
  if (a > MAX_VAL || b > MAX_VAL || c > MAX_VAL) return null;

  return { values: { a: String(a), b: String(b), c: String(c), op } };
}

function randomSimpleEquation(op, rng) {
  switch (op) {
    case "+": { const a = randInt(rng, 1, 40), b = randInt(rng, 1, Math.min(40, MAX_VAL - a)); return { a, b, c: a + b }; }
    case "−": { const a = randInt(rng, 3, 80), b = randInt(rng, 1, a - 1); return { a, b, c: a - b }; }
    case "×": {
      const a = randInt(rng, 2, 12);
      const maxB = Math.min(12, Math.floor(MAX_VAL / a));
      if (maxB < 2) return { a: 2, b: 3, c: 6 };
      const b = randInt(rng, 2, maxB);
      return { a, b, c: a * b };
    }
    case "÷": {
      const b = randInt(rng, 2, 12);
      const maxC = Math.min(12, Math.floor(MAX_VAL / b));
      if (maxC < 2) return { a: 6, b: 2, c: 3 };
      const c = randInt(rng, 2, maxC);
      return { a: b * c, b, c };
    }
  }
}

function divisorsBetween(n, lo, hi) {
  const out = [];
  for (let i = lo; i <= hi; i++) if (n % i === 0) out.push(i);
  return out;
}
